import { useCallback, useEffect, useRef } from 'react';
import { getUploadById } from '@/features/uploads/data';
import { ApiError } from '@/shared/data/client';

type UseResultsFileUploadPollingParams = {
  accessToken: string | null;
  selectedOrganisationId: string | null;
  onRefresh?: () => void;
  invalidateUploadRelatedTables: () => Promise<void>;
};

export const useResultsFileUploadPolling = ({
  accessToken,
  selectedOrganisationId,
  onRefresh,
  invalidateUploadRelatedTables,
}: UseResultsFileUploadPollingParams): ((uploadId: string) => void) => {
  const resultsFilePollRef = useRef<Set<string>>(new Set());
  const resultsFilePollTimeoutRef = useRef<Partial<Record<string, ReturnType<typeof setTimeout>>>>(
    {},
  );
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      Object.values(resultsFilePollTimeoutRef.current).forEach((timeoutId) => {
        clearTimeout(timeoutId);
      });
      resultsFilePollTimeoutRef.current = {};
      resultsFilePollRef.current.clear();
    };
  }, []);

  return useCallback(
    (uploadId: string) => {
      if (resultsFilePollRef.current.has(uploadId)) return;
      resultsFilePollRef.current.add(uploadId);
      const maxAttempts = 20;
      const delayMs = 3000;
      let attempt = 0;
      const stopPolling = (): void => {
        const timeoutId = resultsFilePollTimeoutRef.current[uploadId];
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
          resultsFilePollTimeoutRef.current[uploadId] = undefined;
        }
        resultsFilePollRef.current.delete(uploadId);
      };

      const run = async (): Promise<void> => {
        if (!isMountedRef.current) {
          stopPolling();
          return;
        }
        if (!accessToken) {
          stopPolling();
          return;
        }
        attempt += 1;
        try {
          const upload = await getUploadById(
            uploadId,
            selectedOrganisationId ?? undefined,
            accessToken,
          );
          const resultsFileSlug = upload.resultsFileSlug ?? null;
          if (resultsFileSlug) {
            await invalidateUploadRelatedTables();
            onRefresh?.();
            stopPolling();
            return;
          }
          const status = upload.status;
          const isTerminal =
            status === 'completed' || status === 'completed_with_errors' || status === 'failed';
          if (isTerminal) {
            await invalidateUploadRelatedTables();
            onRefresh?.();
            stopPolling();
            return;
          }
        } catch (error) {
          if (error instanceof ApiError) {
            const authFailed =
              error.status === 401 || error.status === 403 || error.code === 'AUTH_FAILED';
            if (authFailed) {
              stopPolling();
              return;
            }
          }
        }

        if (attempt < maxAttempts) {
          resultsFilePollTimeoutRef.current[uploadId] = setTimeout(() => {
            void run();
          }, delayMs);
          return;
        }
        stopPolling();
      };

      void run();
    },
    [accessToken, invalidateUploadRelatedTables, onRefresh, selectedOrganisationId],
  );
};
